import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { onAuthStateChanged, signOut, User as FirebaseUser } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db, removeUserFromRoom } from '../firebase';
import { setSessionMarker, clearSessionMarker } from '../utils/sessionMarker';

// Tipos de dados
// 1. ATUALIZAÇÃO: 'null' foi substituído por 'login-not-completed'
export type UserRole = "paciente" | "terapeuta" | "participante" | "login-not-completed" | "administrador";

export enum UserRoleEnum {
  PACIENTE = 'paciente',
  TERAPEUTA = 'terapeuta',
  PARTICIPANTE = 'participante',
  LOGIN_NOT_COMPLETED = 'login-not-completed',
  ADMINISTRADOR = 'administrador'
}

export type UserStatus = "ativo" | "suspenso";

// MODIFICAÇÃO: Novos tipos para planos ativos
export type ActivePlans = {
  clubeDaConstelacao?: {
    planId: string;
    purchasedAt: Date;
    expiresAt: Date;
    paymentId: string;
  };
  representante?: {
    usesLeft: number;
    lastPurchasedAt: Date;
    paymentId: string;
  };
};

export type User = {
  name: string;
  email: string;
  uid: string;
  role: UserRole;
  phone?: string;
  avatar?: string;
  status?: UserStatus;
  activePlans?: ActivePlans;
  originalRole?: string; // Adicionar
  temporaryRoleSession?: string; // NOVO CAMPO: Planos ativos do utilizador
  /** Convidado da sala (Firebase Anonymous Auth) — não é conta cadastrada. */
  isAnonymous?: boolean;
};

export type MediaSettings = {
  video: boolean;
  audio: boolean;
};

export type AuthContextType = {
  user: User | null;
  avatar: string;
  mediaSettings: MediaSettings;
  isLoading: boolean;
  firebaseUser: FirebaseUser | null;
  authError: string | null;
  setUser: (user: User | null) => void;
  setAvatar: (avatar: string) => void;
  clearAuthError: () => void;
  setMediaSettings: (settings: MediaSettings) => void;
  updateMediaSettings: (settings: Partial<MediaSettings>) => void;
  updateUserAvatar: (avatarId: string) => Promise<void>;
  refreshAuthUser: () => Promise<void>;
  logout: (roomId?: string) => Promise<void>;
  isAuthenticated: boolean;
  /** true se a sessão atual é convidado anônimo da sala (não cadastro). */
  isAnonymousGuest: boolean;
  isProfileComplete: boolean;
  // NOVAS FUNÇÕES: Para verificar planos ativos
  hasActiveClubeAccess: () => boolean;
  hasRepresentanteUses: () => number;
  canParticipateInSessions: () => boolean;
};

// Valores padrão
const DEFAULT_MEDIA_SETTINGS: MediaSettings = {
  video: true,
  audio: true
};

// Context
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

// NOVA FUNÇÃO: Helper para converter timestamps do Firestore
const convertFirestoreTimestamp = (timestamp: any): Date => {
  if (timestamp && timestamp.toDate) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return new Date();
};

// Provider Component
export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUserState] = useState<User | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [avatar, setAvatarState] = useState<string>('');
  const [mediaSettings, setMediaSettingsState] = useState<MediaSettings>(DEFAULT_MEDIA_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Função para buscar dados do utilizador no Firestore
  const fetchUserData = async (uid: string): Promise<User | null> => {
    try {
      const userDocRef = doc(db, 'users', uid);
      const userDoc = await getDoc(userDocRef);
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        
        // MODIFICAÇÃO: Processar activePlans do Firestore
        let activePlans: ActivePlans | undefined = undefined;
        if (userData.activePlans) {
          activePlans = {};
          
          // Processar Clube da Constelação
          if (userData.activePlans.clubeDaConstelacao) {
            const clube = userData.activePlans.clubeDaConstelacao;
            activePlans.clubeDaConstelacao = {
              planId: clube.planId || 'participante-clube',
              purchasedAt: convertFirestoreTimestamp(clube.purchasedAt),
              expiresAt: convertFirestoreTimestamp(clube.expiresAt),
              paymentId: clube.paymentId || ''
            };
          }
          
          // Processar Ser Representante
          if (userData.activePlans.representante) {
            const rep = userData.activePlans.representante;
            activePlans.representante = {
              usesLeft: rep.usesLeft || 0,
              lastPurchasedAt: convertFirestoreTimestamp(rep.lastPurchasedAt),
              paymentId: rep.paymentId || ''
            };
          }
        }
        
        return {
          uid,
          name: userData.name || '',
          email: userData.email || '',
          role: userData.role || 'login-not-completed',
          phone: userData.phone || userData.telefone || userData.whatsapp || '',
          avatar: userData.avatar || '',
          status: userData.status || 'ativo',
          activePlans,
          isAnonymous: userData.isAnonymous === true,
        };
      } else {
        return null;
      }
    } catch (error) {
      console.error('Erro ao buscar dados do utilizador:', error);
      return null;
    }
  };

  // NOVA FUNÇÃO: refreshAuthUser - Força a atualização dos dados do utilizador
  const refreshAuthUser = async (): Promise<void> => {
    if (!firebaseUser) {
      return;
    }

    try {
      const userData = await fetchUserData(firebaseUser.uid);

      if (userData && userData.role === 'terapeuta' && userData.status === 'suspenso') {
        setAuthError("A sua conta de terapeuta está suspensa. Por favor, contacte o suporte.");
        await signOut(auth);
        setUserState(null);
        setFirebaseUser(null);
        return;
      }

      if (userData) {
        setUserState({
          ...userData,
          isAnonymous: firebaseUser.isAnonymous || userData.isAnonymous === true,
        });
        setAvatarState(userData.avatar || '');
      } else {
        const temporaryUser: User = {
          uid: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          role: 'login-not-completed',
          avatar: '',
          isAnonymous: firebaseUser.isAnonymous,
        };
        setUserState(temporaryUser);
        setAvatarState('');
      }
    } catch (error) {
      console.error('refreshAuthUser: Erro ao atualizar dados do utilizador:', error);
      throw error;
    }
  };

  // Listener de autenticação do Firebase
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true);
      setFirebaseUser(firebaseUser);
      
      if (firebaseUser) {
        const userData = await fetchUserData(firebaseUser.uid);

        if (userData && userData.role === 'terapeuta' && userData.status === 'suspenso') {
          setAuthError("A sua conta de terapeuta está suspensa. Por favor, contacte o suporte.");
          await signOut(auth);
          setUserState(null);
          setFirebaseUser(null);
          setIsLoading(false);
          return;
        }
        
        if (userData) {
          const nextUser: User = {
            ...userData,
            isAnonymous: firebaseUser.isAnonymous || userData.isAnonymous === true,
          };
          setUserState(nextUser);
          setAvatarState(userData.avatar || '');
          // Convidado da sala não marca sessão no site (evita "logado" fora da sala).
          if (!firebaseUser.isAnonymous && !nextUser.isAnonymous) {
            setSessionMarker();
          } else {
            clearSessionMarker();
          }
        } else {
          console.log('Utilizador autenticado mas sem documento no Firestore. Criando estado temporário...');
          const temporaryUser: User = {
            uid: firebaseUser.uid,
            name: firebaseUser.displayName || '',
            email: firebaseUser.email || '',
            role: 'login-not-completed',
            avatar: '',
            isAnonymous: firebaseUser.isAnonymous,
          };
          setUserState(temporaryUser);
          setAvatarState('');
          if (!firebaseUser.isAnonymous) {
            setSessionMarker();
          } else {
            clearSessionMarker();
          }
        }
      } else {
        setUserState(null);
        setAvatarState('');
        setMediaSettingsState(DEFAULT_MEDIA_SETTINGS);
        clearSessionMarker();
      }
      
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const clearAuthError = () => {
    setAuthError(null);
  };

  const setUser = (newUser: User | null) => {
    setUserState(newUser);
  };

  const updateUserAvatar = async (avatarId: string): Promise<void> => {
    if (!user) {
      throw new Error('Utilizador não autenticado');
    }
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, { avatar: avatarId });
      setUserState(currentUser => currentUser ? { ...currentUser, avatar: avatarId } : null);
      setAvatarState(avatarId);
    } catch (error) {
      console.error('Erro ao atualizar avatar no Firestore:', error);
      throw error;
    }
  };

  const setAvatar = (newAvatar: string) => {
    setAvatarState(newAvatar);
    if (user) {
      setUserState(currentUser => currentUser ? { ...currentUser, avatar: newAvatar } : null);
    }
  };

  const setMediaSettings = (settings: MediaSettings) => {
    setMediaSettingsState(settings);
  };

  const updateMediaSettings = (settings: Partial<MediaSettings>) => {
    setMediaSettingsState({ ...mediaSettings, ...settings });
  };

  const logout = async (roomId?: string): Promise<void> => {
    try {
      if (roomId && user?.uid) {
        await removeUserFromRoom(roomId, user.uid);
      }
      clearSessionMarker();
      await signOut(auth);
    } catch (error) {
      console.error('Erro durante logout:', error);
      throw error;
    }
  };

  // NOVAS FUNÇÕES: Para verificar planos ativos
  const hasActiveClubeAccess = (): boolean => {
    if (!user?.activePlans?.clubeDaConstelacao) {
      return false;
    }
    
    const now = new Date();
    const expiresAt = user.activePlans.clubeDaConstelacao.expiresAt;
    return now < expiresAt;
  };

  const hasRepresentanteUses = (): number => {
    if (!user?.activePlans?.representante) {
      return 0;
    }
    
    return user.activePlans.representante.usesLeft || 0;
  };

  const canParticipateInSessions = (): boolean => {
    return hasActiveClubeAccess() || hasRepresentanteUses() > 0;
  };

  // Convidado anônimo da sala mantém `user` para a meeting, mas NÃO conta como
  // login de cliente (checkout, planos, painel, cookie cross-domain).
  const isAnonymousGuest =
    !!firebaseUser?.isAnonymous || user?.isAnonymous === true;
  const isAuthenticated = !!firebaseUser && !!user && !isAnonymousGuest;
  const isProfileComplete = isAuthenticated && 
                          user?.role !== 'login-not-completed' &&
                          !!user?.name;

  const contextValue: AuthContextType = {
    user,
    firebaseUser,
    avatar,
    mediaSettings,
    isLoading,
    authError,
    clearAuthError,
    setUser,
    setAvatar,
    setMediaSettings,
    updateMediaSettings,
    updateUserAvatar,
    refreshAuthUser,
    logout,
    isAuthenticated,
    isAnonymousGuest,
    isProfileComplete,
    // NOVAS FUNÇÕES incluídas no contexto
    hasActiveClubeAccess,
    hasRepresentanteUses,
    canParticipateInSessions
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Hooks personalizados
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const useProfile = () => {
  const { user, avatar, isProfileComplete } = useAuth();
  return { user, avatar, isProfileComplete };
};

export const useMediaSettings = () => {
  const { mediaSettings, setMediaSettings, updateMediaSettings } = useAuth();
  return { mediaSettings, setMediaSettings, updateMediaSettings };
};

export const usePermissions = () => {
  const { user } = useAuth();
  
  const canControlAllAvatars = user?.role === 'terapeuta' || user?.role === 'administrador';
  const hasAvatar = user?.role === 'paciente' || user?.role === 'terapeuta'|| user?.role === 'administrador';
  
  return {
    canControlAllAvatars,
    hasAvatar,
    userRole: user?.role
  };
};

// NOVO HOOK: Para funcionalidades de planos
export const usePlans = () => {
  const { user, hasActiveClubeAccess, hasRepresentanteUses, canParticipateInSessions } = useAuth();
  
  return {
    activePlans: user?.activePlans,
    hasActiveClubeAccess: hasActiveClubeAccess(),
    representanteUsesLeft: hasRepresentanteUses(),
    canParticipateInSessions: canParticipateInSessions(),
    clubeExpiresAt: user?.activePlans?.clubeDaConstelacao?.expiresAt,
    lastRepresentantePurchase: user?.activePlans?.representante?.lastPurchasedAt
  };
};